import type { SegmentMetrics } from '@ohmyprime/p2p-shared';
import type { SegmentTransferRecord } from './types.js';
import { NetworkAccounting, type NetworkSource, type NetworkPath, type NetworkWaste, type NetworkTotals } from './network-accounting.js';

const EMIT_INTERVAL_MS = 5_000;
const WINDOW_SIZE = 200;
const SEGMENT_MEDIA = /^seg\d+\.m4s$/;
/** Offload ratio uses only recent playback deliveries so prefetch/upload seed pulls do not dilute P2P %. */
const PLAYBACK_OFFLOAD_WINDOW = 40;

/** Cumulative playback delivery counters by source. */
export interface DeliveryTotals {
  segmentsFromSeed: number;
  segmentsFromPeers: number;
  segmentsByPeer: Record<string, number>;
  bytesFromSeed: number;
  bytesFromPeers: number;
}

export interface PeerQualitySnapshot {
  peerId: string;
  reliability: number;
  uploadRate: number;
}

/**
 * Tracks segment transfer outcomes and computes running SegmentMetrics.
 * Emits a metrics snapshot every 5 seconds via the provided callback.
 */
export class MetricsCollector {
  private readonly network = new NetworkAccounting();
  recordNetworkBytes(source: NetworkSource, path: NetworkPath, bytes: number): void { this.network.receive(source, path, bytes); }
  recordNetworkWaste(kind: NetworkWaste, bytes: number): void { this.network.discard(kind, bytes); }
  getNetworkTotals(): NetworkTotals { return this.network.snapshot(this.watchedMs); }
  private readonly records: SegmentTransferRecord[] = [];
  private readonly receiveSamples: {bytes:number;ms:number;at:number}[] = [];
  private peerConnectedCount = 0;
  private peerDisconnectedCount = 0;
  private startupTimeMs = 0;
  private latencyToLiveEdgeMs = 0;
  private deadlineMissCount = 0;
  private totalRequests = 0;
  private rebufferMs = 0;
  private watchedMs = 0;
  private readonly deliveryTimesMs: number[] = [];
  private tunedDeadlineMs = 1500;
  private readonly totals: DeliveryTotals = {
    segmentsFromSeed: 0,
    segmentsFromPeers: 0,
    segmentsByPeer: {},
    bytesFromSeed: 0,
    bytesFromPeers: 0,
  };

  /** Fase 1 instrumentation: wall time from when the scheduler was asked for a segment
   *  until we delivered bytes (peer or seed). Used to compute rough E2E receive latency. */
  private requestStartTimes = new Map<string, number>();

  private timer: ReturnType<typeof setInterval> | null = null;
  private onEmit: ((metrics: SegmentMetrics) => void) | null = null;

  start(onEmit: (metrics: SegmentMetrics) => void): void {
    this.onEmit = onEmit;
    this.timer = setInterval(() => {
      this.onEmit?.(this.snapshot());
    }, EMIT_INTERVAL_MS);
  }

  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.onEmit = null;
  }

  private readonly transferMs = new Map<string, number>();
  /** Network duration of the last recorded transfer of `segmentId` (0 when unknown or a cache hit). */
  getTransferMs(segmentId: string): number {
    return this.transferMs.get(segmentId) ?? 0;
  }

  recordSegment(record: SegmentTransferRecord): void {
    if(record.durationMs>0){
      this.transferMs.set(record.segmentId,record.durationMs);
      if(this.transferMs.size>32)this.transferMs.delete(this.transferMs.keys().next().value!);
    }
    // Cache hits have no network duration and must never drive an ABR upswitch.
    if(SEGMENT_MEDIA.test(record.segmentId) && Number.isFinite(record.durationMs) && record.durationMs>0 &&
       Number.isFinite(record.bytesReceived) && record.bytesReceived>0){
      this.receiveSamples.push({bytes:record.bytesReceived,ms:record.durationMs,at:Date.now()});
      if(this.receiveSamples.length>10)this.receiveSamples.shift();
    }
    this.records.push(record);
    this.totalRequests++;
    if (this.records.length > WINDOW_SIZE) {
      this.records.shift();
    }
    if (record.playback === true) {
      if (record.source === 'seed') {
        this.totals.segmentsFromSeed++;
        this.totals.bytesFromSeed += record.bytesReceived;
      } else {
        this.totals.segmentsFromPeers++;
        this.totals.bytesFromPeers += record.bytesReceived;
        if (record.fromPeerId) {
          this.totals.segmentsByPeer[record.fromPeerId] =
            (this.totals.segmentsByPeer[record.fromPeerId] ?? 0) + 1;
        }
      }
    }
    if (record.source === 'peer' && record.durationMs > 0) {
      this.deliveryTimesMs.push(record.durationMs);
      if (this.deliveryTimesMs.length > 50) {
        this.deliveryTimesMs.shift();
      }
      this.autoTuneDeadline();
    }
  }

  /** Byte-weighted observed transfer rate; stale observations cannot imply recovery. */
  getReceiveThroughputBps(): number {
    const now=Date.now();
    const fresh=this.receiveSamples.filter(r=>r.at<=now&&now-r.at<=20000);
    const ms=fresh.reduce((n,r)=>n+r.ms,0);
    return ms>0?fresh.reduce((n,r)=>n+r.bytes,0)*8000/ms:0;
  }

  recordDeadlineMiss(): void {
    this.deadlineMissCount++;
    this.totalRequests++;
  }

  recordRebuffer(durationMs: number): void {
    this.rebufferMs += durationMs;
  }

  recordWatched(durationMs: number): void {
    this.watchedMs += durationMs;
  }

  recordPeerConnected(): void {
    this.peerConnectedCount++;
  }

  recordPeerDisconnected(): void {
    this.peerDisconnectedCount++;
  }

  setStartupTimeMs(ms: number): void {
    this.startupTimeMs = ms;
  }

  setLatencyToLiveEdgeMs(ms: number): void {
    this.latencyToLiveEdgeMs = ms;
  }

  getTunedDeadlineMs(): number {
    return this.tunedDeadlineMs;
  }

  /** Fase 1: call when a segment request enters the scheduler (playback or prefetch). */
  markRequestStart(segmentId: string): void {
    this.requestStartTimes.set(segmentId, Date.now());
  }

  /** Fase 1: call on successful delivery (peer or seed). Returns the latency if known. */
  markRequestDelivered(segmentId: string, _source: 'peer' | 'seed'): number | null {
    const start = this.requestStartTimes.get(segmentId);
    if (!start) return null;
    const latency = Date.now() - start;
    this.requestStartTimes.delete(segmentId);
    // Could feed into a new histogram later; for now just return for logging/metrics
    return latency;
  }

  getTopPeers(limit = 3): PeerQualitySnapshot[] {
    const byPeer = new Map<string, { ok: number; fail: number; bytes: number; ms: number }>();
    for (const r of this.records) {
      if (r.source !== 'peer' || !r.fromPeerId) continue;
      const cur = byPeer.get(r.fromPeerId) ?? { ok: 0, fail: 0, bytes: 0, ms: 0 };
      cur.ok++;
      cur.bytes += r.bytesReceived;
      cur.ms += r.durationMs;
      byPeer.set(r.fromPeerId, cur);
    }
    return Array.from(byPeer.entries())
      .map(([peerId, s]) => ({
        peerId,
        reliability: s.ok / Math.max(1, s.ok + s.fail),
        uploadRate: s.ms > 0 ? (s.bytes / s.ms) * 1000 : 0,
      }))
      .sort((a, b) => b.reliability * b.uploadRate - a.reliability * a.uploadRate)
      .slice(0, limit);
  }

  private autoTuneDeadline(): void {
    if (this.deliveryTimesMs.length < 10) return;
    const sorted = [...this.deliveryTimesMs].sort((a, b) => a - b);
    const p95 = sorted[Math.floor(sorted.length * 0.95)] ?? 1500;
    this.tunedDeadlineMs = Math.min(3000, Math.max(800, Math.ceil(p95 * 1.5)));
  }

  /** Cumulative playback deliveries by source (copy). */
  getDeliveryTotals(): DeliveryTotals {
    return { ...this.totals, segmentsByPeer: { ...this.totals.segmentsByPeer } };
  }

  /** Current latency value (player-reported or engine-derived). */
  getLatencyToLiveEdgeMs(): number {
    return this.latencyToLiveEdgeMs;
  }

  /**
   * p2pOffloadRatio = bytes from non-seed peers / all bytes, over the last
   * PLAYBACK_OFFLOAD_WINDOW playback deliveries. originFallbackRate = seed share.
   */
  snapshot(): SegmentMetrics {
    const total = this.records.length;
    const rebufferRatio =
      this.watchedMs > 0 ? this.rebufferMs / this.watchedMs : 0;
    const segmentDeadlineMissRate =
      this.totalRequests > 0 ? this.deadlineMissCount / this.totalRequests : 0;

    if (total === 0) {
      return {
        p2pOffloadRatio: 0,
        rebufferRatio,
        segmentDeadlineMissRate,
        originFallbackRate: 0,
        averagePeerUploadMbps: 0,
        peerChurnRate: 0,
        startupTimeMs: this.startupTimeMs,
        latencyToLiveEdgeMs: this.latencyToLiveEdgeMs,
      };
    }

    const playbackRecords = this.records.filter((r) => r.playback === true);
    const offloadPool =
      playbackRecords.length > 0
        ? playbackRecords.slice(-PLAYBACK_OFFLOAD_WINDOW)
        : this.records.slice(-PLAYBACK_OFFLOAD_WINDOW);

    let p2pBytes = 0;
    let originBytes = 0;
    for (const r of offloadPool) {
      if (r.source === 'peer') p2pBytes += r.bytesReceived;
      else originBytes += r.bytesReceived;
    }
    const totalBytes = p2pBytes + originBytes;
    const p2pOffloadRatio =
      totalBytes > 0 ? p2pBytes / totalBytes : 0;
    const originFallbackRate =
      totalBytes > 0 ? originBytes / totalBytes : 0;

    const p2pRecords = this.records.filter((r) => r.source === 'peer');
    let averagePeerUploadMbps = 0;
    if (p2pRecords.length > 0) {
      const avgBytesPerMs =
        p2pRecords.reduce(
          (sum, r) => sum + (r.durationMs > 0 ? r.bytesReceived / r.durationMs : 0),
          0,
        ) / p2pRecords.length;
      averagePeerUploadMbps = (avgBytesPerMs * 1000 * 8) / 1_000_000;
    }

    const peerChurnRate =
      this.peerConnectedCount > 0
        ? this.peerDisconnectedCount / this.peerConnectedCount
        : 0;

    return {
      p2pOffloadRatio,
      rebufferRatio,
      segmentDeadlineMissRate,
      originFallbackRate,
      averagePeerUploadMbps,
      peerChurnRate,
      startupTimeMs: this.startupTimeMs,
      latencyToLiveEdgeMs: this.latencyToLiveEdgeMs,
    };
  }
}
